import React from "react";
import { TrendingUp } from "lucide-react";
import { OrderStats, User } from "./types";

interface MonthlyStatsProps {
  stats: OrderStats | null;
  currentUser?: User;
  timePeriod: string;
}

const MonthlyStats: React.FC<MonthlyStatsProps> = ({
  stats,
  currentUser,
  timePeriod,
}) => {
  if (currentUser?.role !== "country-post-manager") {
    return null; // Only show for post office managers
  }

  const getPeriodTitle = () => {
    switch (timePeriod) {
      case "today":
        return "Today's Statistics";
      case "this-week":
        return "This Week Statistics";
      case "this-month":
        return "This Month Statistics";
      case "this-year":
        return "This Year Statistics";
      case "all-time":
        return "All Time Statistics";
      default:
        return "Statistics";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
        {getPeriodTitle()}
      </h3>

      {/* Chart Data */}
      <div className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {stats?.byStatus?.[6] || 0}
            </div>
            <div className="text-sm text-gray-500">Packaging</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {(stats?.byStatus?.[7] || 0) + (stats?.byStatus?.[8] || 0)}
            </div>
            <div className="text-sm text-gray-500">In Transit</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {(stats?.byStatus?.[9] || 0) + (stats?.byStatus?.[10] || 0)}
            </div>
            <div className="text-sm text-gray-500">Delivered</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {stats?.byStatus?.[25] || 0}
            </div>
            <div className="text-sm text-gray-500">Returns</div>
          </div>
        </div>
      </div>

      {/* Visual Chart */}
      <div className="space-y-3">
        {/* Packaging Bar */}
        <div className="flex items-center">
          <div className="w-20 text-sm text-gray-600">Packaging</div>
          <div className="flex-1 mx-4">
            <div className="bg-gray-200 rounded-full h-4">
              <div
                className="bg-purple-500 h-4 rounded-full flex items-center justify-end pr-2"
                style={{
                  width: `${Math.min(
                    100,
                    ((stats?.byStatus?.[6] || 0) /
                      Math.max(1, stats?.total || 1)) *
                      100
                  )}%`,
                }}
              >
                <span className="text-xs text-white font-medium">
                  {stats?.byStatus?.[6] || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* In Transit Bar */}
        <div className="flex items-center">
          <div className="w-20 text-sm text-gray-600">In Transit</div>
          <div className="flex-1 mx-4">
            <div className="bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-500 h-4 rounded-full flex items-center justify-end pr-2"
                style={{
                  width: `${Math.min(
                    100,
                    (((stats?.byStatus?.[7] || 0) +
                      (stats?.byStatus?.[8] || 0)) /
                      Math.max(1, stats?.total || 1)) *
                      100
                  )}%`,
                }}
              >
                <span className="text-xs text-white font-medium">
                  {(stats?.byStatus?.[7] || 0) + (stats?.byStatus?.[8] || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Delivered Bar */}
        <div className="flex items-center">
          <div className="w-20 text-sm text-gray-600">Delivered</div>
          <div className="flex-1 mx-4">
            <div className="bg-gray-200 rounded-full h-4">
              <div
                className="bg-green-500 h-4 rounded-full flex items-center justify-end pr-2"
                style={{
                  width: `${Math.min(
                    100,
                    (((stats?.byStatus?.[9] || 0) +
                      (stats?.byStatus?.[10] || 0)) /
                      Math.max(1, stats?.total || 1)) *
                      100
                  )}%`,
                }}
              >
                <span className="text-xs text-white font-medium">
                  {(stats?.byStatus?.[9] || 0) + (stats?.byStatus?.[10] || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Returns Bar */}
        <div className="flex items-center">
          <div className="w-20 text-sm text-gray-600">Returns</div>
          <div className="flex-1 mx-4">
            <div className="bg-gray-200 rounded-full h-4">
              <div
                className="bg-orange-500 h-4 rounded-full flex items-center justify-end pr-2"
                style={{
                  width: `${Math.min(
                    100,
                    ((stats?.byStatus?.[25] || 0) /
                      Math.max(1, stats?.total || 1)) *
                      100
                  )}%`,
                }}
              >
                <span className="text-xs text-white font-medium">
                  {stats?.byStatus?.[25] || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Total Orders: {stats?.total || 0}</span>
          <span>
            Last Update:{" "}
            {stats?.lastUpdate
              ? new Date(stats.lastUpdate).toLocaleDateString()
              : "Never"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MonthlyStats;
